import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { getProducts, getCategories } from '../../api/products';
import ProductCard from '../../components/customer/ProductCard';
import styles from './CataloguePage.module.css';

export default function CataloguePage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('newest');

  const category = searchParams.get('category') || '';

  const { data: catData } = useQuery({ queryKey: ['categories'], queryFn: getCategories });
  const { data, isLoading } = useQuery({
    queryKey: ['products', category, search],
    queryFn: () => getProducts({ ...(category && { category }), ...(search && { search }) }),
  });

  let products = data?.items || [];
  if (sort === 'price_asc') products = [...products].sort((a: { price: number }, b: { price: number }) => a.price - b.price);
  if (sort === 'price_desc') products = [...products].sort((a: { price: number }, b: { price: number }) => b.price - a.price);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('catalogue.title')}</h1>
      </div>

      <div className={styles.layout}>
        {/* Sidebar filters */}
        <aside className={styles.sidebar}>
          <div className={styles.filterGroup}>
            <h3 className={styles.filterTitle}>{t('catalogue.filter_category')}</h3>
            <button
              className={`${styles.catBtn} ${!category ? styles.active : ''}`}
              onClick={() => setSearchParams({})}
            >
              {t('catalogue.all_categories')}
            </button>
            {(catData?.categories || []).map((cat: { id: string; name: string }) => (
              <button
                key={cat.id}
                className={`${styles.catBtn} ${category === cat.id ? styles.active : ''}`}
                onClick={() => setSearchParams({ category: cat.id })}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </aside>

        {/* Products */}
        <div className={styles.main}>
          <div className={styles.toolbar}>
            <div className={styles.searchBox}>
              <Search size={16} className={styles.searchIcon} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('catalogue.search_placeholder')}
                className={styles.searchInput}
              />
            </div>
            <select
              value={sort}
              onChange={e => setSort(e.target.value)}
              className={styles.sortSelect}
            >
              <option value="newest">{t('catalogue.sort_newest')}</option>
              <option value="price_asc">{t('catalogue.sort_price_asc')}</option>
              <option value="price_desc">{t('catalogue.sort_price_desc')}</option>
            </select>
          </div>

          {isLoading ? (
            <div className={styles.grid}>
              {Array.from({ length: 8 }).map((_, i) => <div key={i} className={styles.skeleton} />)}
            </div>
          ) : products.length === 0 ? (
            <div className={styles.empty}>{t('catalogue.no_products')}</div>
          ) : (
            <div className={styles.grid}>
              {products.map((p: Record<string, unknown>) => <ProductCard key={p.id as string} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
